/** Inline artifact card for ChatBox `data-artifact` parts: a compact card that opens the artifact panel. The
 * panel is the render surface — the card only advertises the artifact (icon by kind + title + "Open"). Unknown
 * kinds get a generic icon and still open (the panel's fallback renderer handles them); intermediate artifacts
 * surface only in the turn's "Working files" disclosure. */

import { Article, BarChart, Code, FileDownloadOutlined, InsertDriveFileOutlined, Notes, TableChart } from "@mui/icons-material"
import type { SvgIconComponent } from "@mui/icons-material"
import { Box, ButtonBase, IconButton, Typography, styled } from "@mui/material"
import type { SxProps, Theme } from "@mui/material"
import type { MouseEvent } from "react"
import type { ChatDataMessagePart } from "@mui/x-chat-headless"
import type { ChatPartRendererProps } from "@mui/x-chat-headless"
import {
  type ArtifactEnvelope,
  type FileArtifact,
  type KnownArtifact,
  isArtifactEnvelope,
  artifactKindLabel,
  isFileArtifact,
  normalizeFileArtifact,
} from "./artifact-contract"
import { isIntermediateArtifact } from "./working-files"
import { useArtifactPanelActions } from "./use-artifact-panel"
import { useArtifactDownload } from "./use-artifact-download"

// Open lookup; the `satisfies` check keeps the known kinds exhaustive so a new kind can't ship silently iconless.
const ARTIFACT_ICONS: Partial<Record<string, SvgIconComponent>> = {
  table: TableChart,
  plot: BarChart,
  text: Article,
  markdown: Notes,
  html: Code,
  file: InsertDriveFileOutlined,
} satisfies Record<KnownArtifact["type"], SvgIconComponent>

// Scheme-aware CSS vars, not theme.palette.* (cssVariables freezes those to light — invisible border + broken hover in dark).
const cardShellStyle = (theme: Theme) => ({
  display: "flex",
  alignItems: "center",
  gap: theme.spacing(1),
  width: "100%",
  maxWidth: 360,
  // Centered in the conversation column, with breathing room above so the first card doesn't hug the paragraph before it.
  marginInline: "auto",
  marginBlock: theme.spacing(2, 0.5),
  border: "1px solid var(--app-overlay-border)",
  borderRadius: theme.shape.borderRadius,
  padding: theme.spacing(1),
  "&:hover": { backgroundColor: "var(--app-subtle-hover-surface)" },
})

const CardRoot = styled(ButtonBase)(({ theme }) => ({
  ...cardShellStyle(theme),
  textAlign: "left",
}))

const IconTile = styled(Box)(({ theme }) => ({
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  flexShrink: 0,
  width: theme.spacing(4),
  height: theme.spacing(4),
  borderRadius: theme.shape.borderRadius,
  backgroundColor: "var(--mui-palette-primary-main)",
  color: "var(--mui-palette-primary-contrastText)",
}))

const TEXT_SX = { flex: 1, minWidth: 0 } satisfies SxProps<Theme>
const LABEL_SX = { fontWeight: 600 } satisfies SxProps<Theme>
const OPEN_SX = { flexShrink: 0, color: "primary.main", fontWeight: 600 } satisfies SxProps<Theme>

const FileCardRoot = styled(Box)(({ theme }) => cardShellStyle(theme))

const FileCardBody = styled(ButtonBase)(({ theme }) => ({
  display: "flex",
  alignItems: "center",
  gap: theme.spacing(1),
  flex: 1,
  minWidth: 0,
  textAlign: "left",
  borderRadius: theme.shape.borderRadius,
}))

function FileArtifactCard({ artifact }: { artifact: FileArtifact }) {
  const actions = useArtifactPanelActions()
  const activeThreadId = actions?.activeThreadId ?? null
  const { downloadArtifact, isDownloading } = useArtifactDownload(artifact, activeThreadId)
  const { filename } = normalizeFileArtifact(artifact)
  const caption = `${artifactKindLabel(artifact)} · click to open`

  const onOpen = (event: MouseEvent<HTMLButtonElement>) => actions?.openArtifact(artifact, event.currentTarget)
  const onDownload = (event: MouseEvent<HTMLButtonElement>) => {
    event.stopPropagation()
    if (activeThreadId == null) return
    downloadArtifact(activeThreadId, artifact.id)
  }

  return (
    <FileCardRoot data-testid="chatbox-artifact-part" data-artifact-type={artifact.type}>
      <FileCardBody onClick={onOpen} aria-label={`Open artifact: ${artifact.title ?? filename}`}>
        <IconTile><InsertDriveFileOutlined fontSize="small" /></IconTile>
        <Box sx={TEXT_SX}>
          <Typography variant="body2" noWrap sx={LABEL_SX}>{filename}</Typography>
          <Typography variant="caption" color="text.secondary">{caption}</Typography>
        </Box>
      </FileCardBody>
      <IconButton
        size="small"
        aria-label={`Download ${filename}`}
        data-testid="chatbox-artifact-part-download"
        disabled={activeThreadId == null || isDownloading}
        onClick={onDownload}
      >
        <FileDownloadOutlined fontSize="small" />
      </IconButton>
    </FileCardRoot>
  )
}

function ArtifactCard({ artifact }: { artifact: ArtifactEnvelope }) {
  const actions = useArtifactPanelActions()
  const Icon = ARTIFACT_ICONS[artifact.type] ?? InsertDriveFileOutlined

  const onOpen = (event: MouseEvent<HTMLButtonElement>) => actions?.openArtifact(artifact, event.currentTarget)

  return (
    <CardRoot data-testid="chatbox-artifact-part" data-artifact-type={artifact.type} onClick={onOpen} aria-label={`Open artifact: ${artifact.title ?? "Artifact"}`}>
      <IconTile><Icon fontSize="small" /></IconTile>
      <Box sx={TEXT_SX}>
        <Typography variant="body2" noWrap sx={LABEL_SX}>{artifact.title ?? "Artifact"}</Typography>
        <Typography variant="caption" color="text.secondary">{`${artifactKindLabel(artifact)} · click to open`}</Typography>
      </Box>
      <Typography variant="caption" sx={OPEN_SX}>Open ›</Typography>
    </CardRoot>
  )
}

export function ArtifactPart({ part }: ChatPartRendererProps<ChatDataMessagePart>) {
  const { data } = part
  if (!isArtifactEnvelope(data)) return null
  // Intermediate artifacts surface only in the turn's "Working files" disclosure, not as an inline card.
  if (isIntermediateArtifact(data)) return null
  if (isFileArtifact(data)) return <FileArtifactCard artifact={data} />
  return <ArtifactCard artifact={data} />
}
